import { useState } from "react";
import type { Attendance, MonthPayment, StudentPayment } from "@edu/shared";
import { localDateAt, periodTitle, periodTitleShort } from "@edu/shared";
import { meetingCountText } from "../../lib/plurals.ts";
import { monthlySummary } from "../../lib/student-facts.ts";

/**
 * Учёт по занятиям: посещения и оплаты. Органы учёта для списка занятий
 * (`StudentOccurrences.tsx`): сводка у заголовка, чипы месяцев, черта и круг
 * посещения.
 *
 * ⚠️ **С 26.09.2026 отдельной карточки «Учёт» нет** (решение владельца): её
 * список занятий слит со списком уроков в одну карточку «Занятия», а здесь
 * остались органы учёта, которые та карточка ставит у себя. Правила ниже
 * переехали вместе с ними и в силе целиком.
 *
 * ⚠️ **Органов учёта нет вовсе, пока преподаватель не включил учёт** (одна
 * ручка на всех учеников, `MeResponse.tracking`). Это и есть механизм «не
 * назойливо»: по умолчанию выключено, и карточка выглядит ровно так, как
 * выглядела до появления учёта. Колонок «Был» и «Оплата», пустых и молчащих, в
 * списке тогда нет: пустое место на экране это тоже сообщение, и читается оно
 * как «сюда надо что-то внести».
 *
 * ⚠️ **Список состоит из ЗАНЯТИЙ, а не из уроков**, и это не синоним. У занятия
 * нет строки в базе, пока оно не стало уроком, а в режиме сосуществования
 * (занятие идёт в Zoom по внешней ссылке, раздел 2 CLAUDE.md, штатная
 * конфигурация продукта) уроком оно не становится никогда. Список из уроков был
 * бы пуст навсегда ровно у того преподавателя, ради которого учёт и делается.
 * Поэтому у строки бывает пустое название: занятие было, урока не было.
 *
 * ⚠️ **Долг не красится тревожным цветом.** Просьба звучала прямо: «не
 * назойливо» это про то, чтобы не показывать счётчик алым. Преподаватель может
 * просто знать об оплате и не отмечать, и красная цифра сделала бы из его
 * привычки ошибку.
 *
 * ⚠️ **У помесячной пары единица оплаты месяц, и колонки поурочной оплаты у неё
 * нет вовсе.** Вместо неё ряд чипов месяцев над списком. Колонка посещаемости
 * при этом остаётся: «был на занятии» это факт о занятии, а не о деньгах, и от
 * режима оплаты он не зависит.
 *
 * ⚠️ **У чипов месяцев НЕТ подтверждения** (решение владельца 21.08.2026).
 * Нажатие ставит, повторное снимает. Проверка на необходимость подтверждения
 * одна: обратимо ли действие в один щелчок. Здесь обратимо, и подтверждение
 * платило бы щелчком за каждое ПРАВИЛЬНОЕ действие ради редкого ошибочного.
 * Тот же приём, что у чипов посещаемости и поурочной оплаты в строке.
 */

/**
 * Подпись у заголовка. Одна строка на оба вида учёта, потому что включены они
 * бывают порознь, и «не оплачен сентябрь · пропусков 1» это два независимых
 * ответа.
 *
 * ⚠️ У помесячной пары ЕДИНИЦА ДОЛГА МЕСЯЦ, а не занятие, и складывать их
 * нельзя. Поэтому здесь два разных предложения, а не одно с подстановкой:
 * «не отмечено 3 занятия» и «не оплачен сентябрь» отвечают на разные вопросы.
 *
 * ⚠️ Слова «просрочен» нет и не будет: срока мы не знаем (какого числа семья
 * договорилась переводить деньги, продукту неизвестно), а «не оплачен» это
 * факт. Разбор в `monthlyState`.
 */
export function summaryText(
  payment: StudentPayment,
  tracking: { attendance: boolean; payments: boolean },
): string {
  const parts: string[] = [];

  if (tracking.payments) {
    if (payment.mode === "monthly") {
      parts.push(monthlySummary(payment));
    } else if (payment.mode === "none") {
      parts.push("оплаты не веду");
    } else if (payment.unpaidCount !== null) {
      parts.push(
        payment.unpaidCount === 0
          ? "всё отмечено"
          : `не отмечено ${meetingCountText(payment.unpaidCount)}`,
      );
    }
  }

  if (tracking.attendance && payment.absentCount !== null && payment.absentCount > 0) {
    parts.push(`пропусков ${payment.absentCount}`);
  }

  return parts.join(" · ");
}

/**
 * Месяцы чипами: за какие получена оплата.
 *
 * ⚠️ **Без подтверждения**: нажатие ставит, повторное снимает. Разбор в шапке
 * файла. Ошибка исправляется тем же щелчком по тому же месту, поэтому окно
 * подтверждения тут было бы платой за каждое правильное действие.
 *
 * ⚠️ **Порядок хронологический, слева направо.** Месяцы читаются как лента
 * времени, и «новые слева», уместное в списке занятий, здесь читалось бы задом
 * наперёд.
 *
 * ⚠️ **Тревожного цвета нет**: оплаченный месяц зелёный, неоплаченный обычный.
 * Решение владельца, то же, что у поурочного долга.
 */
export function MonthChips(props: {
  months: readonly MonthPayment[];
  /**
   * Месяц «сейчас» ИЗ ОТВЕТА, а не посчитанный здесь.
   *
   * ⚠️ Считать его клиентом было нечем: чипы сервер строит в зоне СЕРИИ, а
   * браузер знает только зону смотрящего, и у пары без расписания зона серии
   * это запасной UTC. Два календаря на одном экране давали занятие под чипом
   * «авг» при подчёркнутом «сен».
   */
  currentPeriod: string | null;
  busy: boolean;
  onMonth: (period: string, paid: boolean) => void;
}) {
  return (
    <div className="months">
      {props.months.map((m) => (
        <button
          key={m.period}
          type="button"
          className="chip months__chip"
          data-tone={m.paidAt !== null ? "ok" : undefined}
          /*
            Текущий месяц помечен подчёркиванием, а не цветом: при предоплате
            именно он и есть точка отсчёта («за этот уже должны были заплатить»),
            и потерять его среди шести одинаковых чипов легко.
          */
          data-now={m.period === props.currentPeriod}
          disabled={props.busy}
          aria-pressed={m.paidAt !== null}
          title={
            m.paidAt !== null
              ? `${periodTitle(m.period)}: оплачен, нажмите чтобы снять`
              : `${periodTitle(m.period)}: отметить оплаченным`
          }
          onClick={() => props.onMonth(m.period, m.paidAt === null)}
        >
          <span>{periodTitleShort(m.period)}</span>
          {/*
            Число занятий второй строкой, приглушённо. Оно не про оплату, оно
            отвечает на соседний вопрос «а сколько их там», из которого
            преподаватель считает сумму.

            ⚠️ Ноль печатается СЛОВОМ «пусто», и слово это выбрано с третьей
            попытки. «0» рядом с «4» читается как счётчик, то есть теряется. «нет»
            читалось ещё хуже: чип отвечает на вопрос «оплачен ли», и «нет» под
            названием месяца читается как ответ на него, а не как «занятий не
            было». «пусто» описывает СОДЕРЖИМОЕ месяца и ответом про оплату быть
            не может.

            ⚠️ Прочерк тут не годится по соседству: им уже помечена
            неотмеченная посещаемость в списке занятий ниже, в той же карточке.
          */}
          <span className="chip__n months__count">{m.lessons === 0 ? "пусто" : m.lessons}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * «Отметить оплаченными все занятия до…».
 *
 * Заведено вместо отвергнутой точки отсчёта (решение владельца 21.08.2026).
 * Разница принципиальная и она видна прямо здесь: точка отсчёта прятала старые
 * занятия сама, а это действие человека, после которого в списке ниже
 * появляются настоящие отметки, каждую из которых видно и можно снять по одной.
 *
 * ⚠️ Дата предзаполнена СЕГОДНЯШНЕЙ в зоне смотрящего, а граница строгая: «до
 * сегодня» закрывает всё вчерашнее и раньше, не трогая сегодняшнее занятие.
 * Так это и читается словами.
 */
export function SettleLine(props: {
  zone: string;
  now: number;
  busy: boolean;
  onSettle: (before: string) => void;
}) {
  const today = localDateAt(props.now, props.zone);
  const [before, setBefore] = useState(today);

  return (
    <div className="form form--row">
      <label className="lbl" htmlFor="settle-before">
        Отметить оплаченными всё до
      </label>
      <input
        className="fld"
        id="settle-before"
        type="date"
        value={before}
        max={today}
        onChange={(e) => setBefore(e.target.value)}
      />
      <button
        type="button"
        className="btn"
        disabled={props.busy || before === ""}
        onClick={() => props.onSettle(before)}
      >
        Подвести черту
      </button>
    </div>
  );
}

/**
 * Следующее состояние посещения по кругу.
 *
 * Отдельной функцией, потому что порядок здесь смысловой: сначала «был», и
 * только потом «не пришёл». Обратный порядок заставлял бы дважды нажимать на
 * частый случай ради редкого.
 */
export function nextAttendance(current: Attendance | null): Attendance | null {
  if (current === null) return "present";
  if (current === "present") return "absent";
  return null;
}
